import { useState, useEffect } from 'react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Bell, BellOff, Check, CheckCheck, Trash2, Settings } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const utils = trpc.useUtils();

  const { data: unreadNotifications } = trpc.taskManager.notifications.listUnread.useQuery(
    undefined,
    { refetchInterval: 30000 } // Refetch a cada 30s
  );

  const markAsRead = trpc.taskManager.notifications.markAsRead.useMutation({
    onSuccess: () => {
      utils.taskManager.notifications.listUnread.invalidate();
    },
  });

  const markAllAsRead = trpc.taskManager.notifications.markAllAsRead.useMutation({
    onSuccess: () => {
      utils.taskManager.notifications.listUnread.invalidate();
      toast.success('Todas as notificações marcadas como lidas');
    },
  });

  const deleteNotification = trpc.taskManager.notifications.delete.useMutation({
    onSuccess: () => {
      utils.taskManager.notifications.listUnread.invalidate();
    },
  });

  // Verificar se push notifications estão habilitadas
  useEffect(() => {
    if ('Notification' in window) {
      setPushEnabled(Notification.permission === 'granted');
    }
  }, []);

  // Solicitar permissão para push notifications
  const requestPushPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Seu navegador não suporta notificações push');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setPushEnabled(true);
        toast.success('Notificações push habilitadas');
      } else {
        toast.error('Permissão para notificações negada');
      }
    } catch (error) {
      console.error('Erro ao solicitar permissão:', error);
      toast.error('Erro ao habilitar notificações');
    }
  };

  // Enviar notificação push quando houver novas notificações
  useEffect(() => {
    if (pushEnabled && unreadNotifications && unreadNotifications.length > 0) {
      const latestNotification = unreadNotifications[0];
      
      // Verificar se já foi mostrada (usando localStorage)
      const lastShownId = localStorage.getItem('lastShownNotificationId');
      if (lastShownId !== String(latestNotification.id)) {
        new Notification(latestNotification.title, {
          body: latestNotification.message || '',
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: `notification-${latestNotification.id}`,
        });
        
        localStorage.setItem('lastShownNotificationId', String(latestNotification.id));
      }
    }
  }, [pushEnabled, unreadNotifications]);

  const unreadCount = unreadNotifications?.length || 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-96 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold">Notificações</h4>
            {unreadCount > 0 && (
              <Badge variant="secondary">{unreadCount}</Badge>
            )}
          </div>
          <div className="flex gap-1">
            {!pushEnabled ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={requestPushPermission}
                title="Habilitar notificações push"
              >
                <BellOff className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                disabled
                title="Notificações push habilitadas"
              >
                <Bell className="h-4 w-4 text-green-600" />
              </Button>
            )}
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => markAllAsRead.mutate()}
                title="Marcar todas como lidas"
              >
                <CheckCheck className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <ScrollArea className="max-h-96">
          {!unreadNotifications || unreadNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Bell className="h-12 w-12 mb-2 opacity-50" />
              <p>Nenhuma notificação</p>
            </div>
          ) : (
            <div className="divide-y">
              {unreadNotifications.map((notification: any) => (
                <div
                  key={notification.id}
                  className="p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <h5 className="font-medium text-sm mb-1">
                        {notification.title}
                      </h5>
                      {notification.message && (
                        <p className="text-sm text-muted-foreground">
                          {notification.message}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => markAsRead.mutate({ id: notification.id })}
                        title="Marcar como lida"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteNotification.mutate({ id: notification.id })}
                        title="Deletar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(notification.created_at), {
                      addSuffix: true,
                      locale: ptBR,
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {!pushEnabled && (
          <div className="p-4 border-t bg-muted/50">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={requestPushPermission}
            >
              <Bell className="h-4 w-4 mr-2" />
              Habilitar Notificações Push
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
